import { useEffect, useMemo, useState } from "react";
import { ColDef } from "ag-grid-community";
import { useLoaderData, useNavigate, useParams, useRouteLoaderData } from "react-router-dom";
import {
  Facility,
  ParkerDetails,
  ParkerInfo,
  ParkerSubInfo,
  SubscriptionParker,
} from "./Data/ApiTypes";
import { PageBar } from "./Controls/PageBar";
import { Button } from "./Controls/Button";
import { DataGrid } from "./Controls/DataGrid";
import { Col, Container, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { gridOneCol } from "./Data/Common";
import { Hyperlink } from "./Controls/Hyperlink";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { toast } from "react-toastify";
import {
  InviteParkerForm,
  InviteParkerModal,
} from "./UserPlanDetailsPageInviteParkerModal";
import ParkerEditModal from "./ParkerEditModal";
import { parseParkerInfo } from "./Data/ApiParse";
import { SubscriptionStatus } from "./Data/ApiTransport";
import { XCircle } from "react-bootstrap-icons";
import { useHandleNavigation } from "./shared/navigation";
import { CancelParkerModal } from "./Controls/CancelParkerModal";
import { useAuthorization } from "./hooks/useAuthorization";
import { emitSubscriptionParkerRemoveReq } from "./Data/ApiEmit";
import {
  formatParkerCancelDate,
  getDefaultCancelParkerDate,
  getMinCancelParkerDate,
  isCancelDateToday,
  toRemovedWhenInstant,
} from "./Data/cancelParker";
import { localDateToInstantUtc } from "./Data/Date";

function guestRowToSubscriptionParker(row: GuestParkerRow): SubscriptionParker {
  return {
    parker: row.parker,
    quantity: row.quantity,
    vehicles: row.vehicles,
    subscriptionGroupId: row.subscriptionGroupId,
    subscriptionGroupName: "",
    startDate: null,
    removedWhen: row.removedWhen,
  };
}

type GuestParkerRow = {
  subscriptionId: number;
  sub: ParkerSubInfo;
  parkerId: number;
  name: string;
  email: string;
  lpn: string;
  plan: string;
  planId: number;
  isManaged: boolean;
  quantity: number;
  parker: ParkerInfo;
  vehicles: SubscriptionParker["vehicles"];
  subscriptionGroupId?: number;
  removedWhen: SubscriptionParker["removedWhen"];
};

export function ParkersPage() {
  const parkers = useLoaderData() as ParkerDetails;
  const facility = useRouteLoaderData("facility") as Facility;
  const facilityTimeZone = facility.timeZone;
  const { facilityId, parkerId } = useParams();
  const [allGuestParkers, setAllGuestParkers] = useState<GuestParkerRow[]>([]);
  const [showCancelParker, setShowCancelParker] = useState(false);
  const [cancelParkerTarget, setCancelParkerTarget] = useState<GuestParkerRow | null>(null);
  const [showInvite, setShowInviteParker] = useState(false);
  const [showParkerEditModal, setShowParkerEditModal] = useState(false);
  const [selectedParker, setSelectedParker] = useState<SubscriptionParker>();
  const [selectedParkerSub, setSelectedParkerSub] = useState<ParkerSubInfo>();

  const nav = useNavigate();
  const { canPerformAction } = useAuthorization();
  const canManageMonthly = canPerformAction('manage-monthly');

  useEffect(() => {
    if (!parkers || !Array.isArray(parkers.subscriptions)) return;

    const guests = parkers.subscriptions.flatMap((sub) =>
      sub.guests.map((guest) => ({
        subscriptionId: sub.subscriptionId,
        sub: sub,
        parkerId: guest.parker.parkerId,
        name: guest.parker.name,
        email: guest.parker.emailAddress,
        lpn:
          guest.vehicles.length > 0
            ? guest.vehicles.map((v) => v.licensePlateNumber).join(" | ")
            : "N/A",
        plan: sub.plan.name,
        planId: sub.plan.planId,
        isManaged: guest.parker.isManaged,
        quantity: guest.quantity,
        parker: guest.parker,
        vehicles: guest.vehicles,
        subscriptionGroupId: guest.subscriptionGroupId,
        removedWhen: guest.removedWhen,
      }))
    );

    setAllGuestParkers(guests);
  }, [parkers]);

  const plans = parkers.subscriptions
    .filter((x) => x.status == SubscriptionStatus.Active)
    .filter(
      (x, index, self) =>
        index ===
        self.findIndex((sub) => sub.subscriptionId === x.subscriptionId)
    )
    .filter((x) => !x.isUsageBasedBilling)
    .map((x) => ({
      id: x.plan.planId,
      name: x.plan.name,
      subId: x.subscriptionId,
      isUsageBasedBilling: x.isUsageBasedBilling,
    }));

  function parkerInvite(data: InviteParkerForm) {
    const targetPlan = plans.find((x) => x.id == data.planId);

    Api.subParkerInvite(parseInt(facilityId!), targetPlan!.subId, {
      emailAddress: data.emailAddress,
      name: data.name,
      quantity: 1,
      subscriptionId: targetPlan!.subId,
      isLoginEnabled: data.isLoginEnabled,
      subscriptionGroupId: null,
      pendingIntegrationCredential: data.pendingIntegrationCredential,
    }).then((resp) =>
      processResult(
        resp,
        (val) => {
          setAllGuestParkers([
            ...allGuestParkers,
            {
              subscriptionId: targetPlan!.subId,
              parkerId: val.parker.parkerId,
              name: val.parker.name,
              lpn: "N/A",
              plan: targetPlan!.name,
              email: val.parker.emailAddress,
              quantity: val.quantity,
              isManaged: val.parker.isManaged,
              parker: parseParkerInfo(val.parker),
              vehicles: [],
              removedWhen: null,
              sub: {
                subscriptionId: targetPlan?.subId,
                status: SubscriptionStatus.Active,
                facilityHasIntegrationsEnabled:
                  selectedParkerSub?.facilityHasIntegrationsEnabled,
              } as ParkerSubInfo,
              planId: targetPlan!.id,
            },
          ]);
        },
        (str) => {
          toast.error(str);
        }
      )
    );
  }

  function parkerCancel(row: GuestParkerRow, cancellationDate: string): void {
    const removedWhenInstant = toRemovedWhenInstant(cancellationDate);

    Api.subParkerRemove(parseInt(facilityId!), row.subscriptionId, emitSubscriptionParkerRemoveReq({
      parkerId: row.parkerId,
      removedWhen: localDateToInstantUtc(cancellationDate),
    })).then((resp) =>
      processResult(
        resp,
        () => {
          if (isCancelDateToday(cancellationDate)) {
            setAllGuestParkers(
              allGuestParkers.filter((p) => p.parker.parkerId !== row.parker.parkerId)
            );
          } else {
            setAllGuestParkers(
              allGuestParkers.map((p) =>
                p.parkerId === row.parkerId
                  ? { ...p, removedWhen: removedWhenInstant }
                  : p
              )
            );
          }
          setShowCancelParker(false);
          setCancelParkerTarget(null);
        },
        (str) => {
          toast.error(str);
        }
      )
    );
  }

  const openCancelParkerModal = (row: GuestParkerRow) => {
    setCancelParkerTarget(row);
    setShowCancelParker(true);
  };

  const handleNavigation = useHandleNavigation();

  const sortedGuests = useMemo(
    () => [...allGuestParkers].sort((a, b) => a.name.localeCompare(b.name)),
    [allGuestParkers]
  );

  const columns = useMemo<ColDef[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 120,
        cellRenderer: (params: any) => {
          const name = params.value;

          return (
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {canManageMonthly ? (
              <Hyperlink
                onClick={() => {
                  setSelectedParker(guestRowToSubscriptionParker(params.data));
                  setSelectedParkerSub(params.data.sub);
                  setShowParkerEditModal(true);
                }}
              >
                {name}
              </Hyperlink>
              ) : (
                name
              )}
            </span>
          );
        },
      },
      {
        field: "managedBy",
        headerName: "Type",
        sortable: true,
        filter: true,
        width: 120,
        maxWidth: 120,
        valueGetter: (params: any) => {
          return params.data.isManaged ? "Self" : "Group";
        },
        cellRenderer: (params: any) => {
          const isManaged = params.data.isManaged;
          return (
            <span
              className={`badge ${isManaged ? "bg-primary" : "bg-secondary"}`}
              style={{ whiteSpace: "nowrap" }}
            >
              {isManaged ? "Self" : "Group"}
            </span>
          );
        },
      },
      {
        field: "email",
        headerName: "Email",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 150,
        valueGetter: (p) =>
          p.data.parker.isAutoGeneratedEmail ? "" : p.data.email,
      },
      {
        field: "lpn",
        headerName: "LPN",
        width: 150,
        maxWidth: 150,
        valueGetter: (params) => {
          const vehicles = params.data.vehicles || [];
          return vehicles.length > 0
            ? vehicles.map((v: any) => v.licensePlateNumber).join(" | ")
            : "N/A";
        },
      },
      {
        field: "cancelDate",
        headerName: "Cancel Date",
        sortable: true,
        filter: true,
        width: 120,
        maxWidth: 120,
        valueGetter: (params: any) =>
          formatParkerCancelDate(params.data.removedWhen, facilityTimeZone),
      },
      {
        field: "plan",
        headerName: "Plan",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 120,
        cellRenderer: (params: any) => {
          const plan = params.value;
          const subscriptionId = params.data.subscriptionId;
          const url = `/facility/${facilityId}/parker/${parkerId}/plan/${subscriptionId}`;

          return (
            <a
              onClick={(e) => {
                e.preventDefault();
                handleNavigation(url, {
                  from: "parkersList",
                  parentParkerId: parkerId,
                });
              }}
              href={url}
              style={{
                color: "#d60087",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {plan}
            </a>
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        sortable: false,
        filter: false,
        width: 80,
        maxWidth: 80,
        cellRenderer: (params: any) => {
          if (params.data.subscriptionGroupId) {
            return (
              <OverlayTrigger
                placement="top"
                delay={{ show: 200, hide: 0 }}
                overlay={
                  <Tooltip id="cancel-disabled-tooltip">
                    Parker must be managed within group.
                  </Tooltip>
                }
              >
                <span className="text-muted small">—</span>
              </OverlayTrigger>
            );
          }
          return (
            <button
              type="button"
              className="btn btn-sm"
              title="Cancel Parker"
              onClick={() => openCancelParkerModal(params.data)}
            >
              <XCircle className="text-muted" />
            </button>
          );
        },
      },
    ],
    [facilityId, parkerId, handleNavigation, canManageMonthly]
  );

  return (
    <Container fluid className="h-100">
      <Row className="h-100">
        <Col style={gridOneCol("max-content 1fr 5px")}>
          <div className="d-flex justify-content-between align-items-center mt-2 mb-2">
            <PageBar title="Parkers" />
            {canManageMonthly && (
            <Button
              className="btn-main"
              onClick={() => setShowInviteParker(true)}
            >
              Add Parker
            </Button>
            )}
          </div>

          <div className="d-none d-md-block" style={{ height: "100%", width: "100%" }}>
            <DataGrid rowData={sortedGuests} columnDefs={columns} />
          </div>

          <div className="d-md-none">
            {sortedGuests.length === 0 ? (
              <div className="text-muted small">No parkers.</div>
            ) : (
              sortedGuests.map((p) => (
                <div key={p.parkerId} className="card mb-3 rounded-4 border-1">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-start gap-3">
                      <div className="flex-grow-1 min-width-0">
                        {canManageMonthly ? (
                        <Hyperlink
                          onClick={() => {
                            setSelectedParker(guestRowToSubscriptionParker(p));
                            setSelectedParkerSub(p.sub);
                            setShowParkerEditModal(true);
                          }}
                        >
                          <span className="fw-bold">{p.name}</span>
                        </Hyperlink>
                        ) : (
                          <span className="fw-bold">{p.name}</span>
                        )}
                        <div className="d-flex align-items-center gap-2 mt-1">
                          <span
                            className={`badge ${p.isManaged ? "bg-primary" : "bg-secondary"}`}
                            style={{ fontSize: "0.7rem" }}
                          >
                            {p.isManaged ? "Self" : "Group"}
                          </span>
                        </div>
                        <div className="text-muted small mt-1">
                          Email:{" "}
                          <span className="fw-semibold">
                            {p.parker.isAutoGeneratedEmail ? "" : p.email}
                          </span>
                        </div>
                        <div className="text-muted small mt-1">
                          LPN: <span className="fw-semibold">{p.lpn}</span>
                        </div>
                        <div className="text-muted small mt-1">
                          Cancel Date:{" "}
                          <span className="fw-semibold">
                            {formatParkerCancelDate(p.removedWhen, facilityTimeZone)}
                          </span>
                        </div>
                        <div className="text-muted small mt-1">
                          Plan:{" "}
                          <a
                            href={`/facility/${facilityId}/parker/${parkerId}/plan/${p.subscriptionId}`}
                            onClick={(e) => {
                              e.preventDefault();
                              handleNavigation(
                                `/facility/${facilityId}/parker/${parkerId}/plan/${p.subscriptionId}`,
                                { from: "parkersList", parentParkerId: parkerId }
                              );
                            }}
                            style={{
                              color: "#d60087",
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                          >
                            {p.plan}
                          </a>
                        </div>
                      </div>
                      {p.subscriptionGroupId ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`group-only-${p.parkerId}`}>
                              Parker must be managed within group.
                            </Tooltip>
                          }
                        >
                          <span className="text-muted small">—</span>
                        </OverlayTrigger>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm flex-shrink-0"
                          title="Cancel Parker"
                          onClick={() => openCancelParkerModal(p)}
                        >
                          <XCircle className="text-muted" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Col>
      </Row>

      {canManageMonthly && (
      <InviteParkerModal
        show={showInvite}
        setShow={setShowInviteParker}
        submit={parkerInvite}
        facilityId={parseInt(facilityId!)}
        plans={plans}
        isEnhancedGroup={false}
      />
      )}

      <CancelParkerModal
        show={showCancelParker}
        onHide={() => {
          setShowCancelParker(false);
          setCancelParkerTarget(null);
        }}
        minCancellationDate={getMinCancelParkerDate()}
        defaultCancellationDate={
          cancelParkerTarget
            ? getDefaultCancelParkerDate({
                removedWhen: cancelParkerTarget.removedWhen,
                facilityTimeZone,
              })
            : getMinCancelParkerDate()
        }
        onConfirm={(cancellationDate) => {
          if (cancelParkerTarget) {
            parkerCancel(cancelParkerTarget, cancellationDate);
          }
        }}
      />

      {selectedParker && (
        <ParkerEditModal
          show={showParkerEditModal}
          onClose={() => setShowParkerEditModal(false)}
          parker={selectedParker!}
          setParker={setSelectedParker}
          parkers={allGuestParkers}
          setParkers={setAllGuestParkers}
          selectedParkerSub={selectedParkerSub!}
          facilityId={parseInt(facilityId!)}
        />
      )}
    </Container>
  );
}
